import { supabase } from "@/api/supabaseClient";

export type HandoverStatus =
  | "pending"
  | "accepted"
  | "cancelled"
  | "rejected"
  | "admin_forced";

export type HandoverRow = {
  id: string;
  organization_id: string;
  case_id: string;
  from_care_site_id: string;
  to_care_site_id: string;
  to_place_id: string | null;
  initiated_by: string;
  initiated_at: string;
  status: HandoverStatus;
  care_entry_id: string | null;
  handover_batch_id: string | null;
  note: string | null;
  accepted_by: string | null;
  accepted_at: string | null;
};

export type PendingHandover = HandoverRow & {
  animal_name: string | null;
  case_number: string | null;
};

export class HandoverApi {
  static async listPending(): Promise<HandoverRow[]> {
    const { data, error } = await supabase.rpc("list_my_pending_handovers");
    if (error) throw error;
    return (data ?? []) as HandoverRow[];
  }

  /** Tiername aus der bestehenden Akte, ohne Schemaänderung. */
  static async listPendingLabeled(): Promise<PendingHandover[]> {
    const rows = await HandoverApi.listPending();
    if (rows.length === 0) return [];
    const ids = [...new Set(rows.map((row) => row.case_id))];
    const { data, error } = await supabase
      .from("animal_cases")
      .select("id, name, case_number")
      .in("id", ids);
    const byId = new Map(
      (error ? [] : (data ?? [])).map((row) => [
        row.id,
        { name: row.name, case_number: row.case_number },
      ]),
    );
    return rows.map((row) => {
      const animal = byId.get(row.case_id);
      return {
        ...row,
        animal_name: animal?.name ?? null,
        case_number: animal?.case_number ?? null,
      };
    });
  }

  static async listForCase(caseId: string): Promise<HandoverRow[]> {
    const { data, error } = await supabase.rpc("list_case_handovers", {
      p_case_id: caseId,
    });
    if (error) throw error;
    return (data ?? []) as HandoverRow[];
  }

  static async initiate(input: {
    caseIds: string[];
    toCareSiteId: string;
    toPlaceId?: string | null;
    note?: string | null;
    batchId?: string | null;
  }): Promise<HandoverRow[]> {
    const { data, error } = await supabase.rpc("initiate_handovers", {
      p_case_ids: input.caseIds,
      p_to_care_site_id: input.toCareSiteId,
      p_to_place_id: input.toPlaceId ?? null,
      p_note: input.note ?? null,
      p_handover_batch_id: input.batchId ?? null,
      p_client_handover_ids: null,
    });
    if (error) throw error;
    return (data ?? []) as HandoverRow[];
  }

  static async accept(handoverId: string, toPlaceId?: string | null) {
    const { data, error } = await supabase.rpc("accept_handover", {
      p_handover_id: handoverId,
      p_to_place_id: toPlaceId ?? null,
    });
    if (error) throw error;
    return data as HandoverRow;
  }

  static async acceptBatch(batchId: string, toPlaceId?: string | null) {
    const { data, error } = await supabase.rpc("accept_handover_batch", {
      p_handover_batch_id: batchId,
      p_to_place_id: toPlaceId ?? null,
    });
    if (error) throw error;
    return (data ?? []) as HandoverRow[];
  }

  static async cancel(handoverId: string, reason?: string | null) {
    const { data, error } = await supabase.rpc("cancel_handover", {
      p_handover_id: handoverId,
      p_note: reason ?? null,
    });
    if (error) throw error;
    return data as HandoverRow;
  }

  static async reject(handoverId: string, reason?: string | null) {
    const { data, error } = await supabase.rpc("reject_handover", {
      p_handover_id: handoverId,
      p_note: reason ?? null,
    });
    if (error) throw error;
    return data as HandoverRow;
  }

  static async force(input: {
    handoverId?: string | null;
    caseId?: string | null;
    toCareSiteId?: string | null;
    toPlaceId?: string | null;
    note?: string | null;
  }) {
    const { data, error } = await supabase.rpc("force_handover", {
      p_handover_id: input.handoverId ?? null,
      p_case_id: input.caseId ?? null,
      p_to_care_site_id: input.toCareSiteId ?? null,
      p_to_place_id: input.toPlaceId ?? null,
      p_note: input.note ?? null,
    });
    if (error) throw error;
    return data as HandoverRow;
  }
}
